import { format, isToday } from "date-fns"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import CancelDockwaPlusMembershipModal from "src/main/Account/Settings/CancelDockwaPlusMembershipModal"
import {
  DockwaPlusProvider,
  useDockwaPlusContext,
} from "src/main/Account/Settings/DockwaPlusContext"
import { DockwaPlusMembershipCard } from "src/main/Account/Settings/DockwaPlusMembershipCard"
import DockwaPlusMembershipSignupModal from "src/main/Account/Settings/DockwaPlusMembershipSignupModal"
import { SettingsContext } from "src/main/Account/Settings/SettingsContext"

import AlertBanner from "src/components/AlertBanner"
import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import Button from "src/components/Button"
import HeadingModalTitle from "src/components/Heading/HeadingModalTitle"
import LineItems from "src/components/LineItems/LineItems"
import SelectPaymentMethod, {
  ADD_NEW_PAYMENT_METHOD,
} from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import {
  queryDockwaPlusSubscriptionInvoicingData,
  restoreDockwaPlusMembership,
  updateDockwaPlusPaymentMethod,
} from "src/api/Account/Settings"

import { useLocalStorageState } from "src/hooks/use_local_storage_state"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { humanize } from "src/utils/string_helpers"

const DockwaPlusSubscriptionContent = ({ user, setUser }) => {
  const { paymentMethods, setPaymentMethods } = useContext(SettingsContext)
  const cardPaymentMethods = paymentMethods.filter(
    (card) => card.type === "PaymentMethod::Card"
  )
  const tracker = useTracker()
  const queryClient = useQueryClient()
  const [membershipPageVisited, setMembershipPageVisited] =
    useLocalStorageState("account", "membershipPageVisited")
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false)
  const [invoicingDataError, setInvoicingDataError] = useState(null)
  const isScheduledForCancellation =
    user.dockwaPlusSubscription?.cancelAtPeriodEnd
  const isPastDue = user.dockwaPlusSubscription?.status === "past_due"
  const currentSubscriptionPaymentMethod = paymentMethods.find(
    (paymentMethod) =>
      paymentMethod.stripePaymentMethodId ===
      user.dockwaPlusSubscription?.stripePaymentMethodId
  )
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(
    currentSubscriptionPaymentMethod?.stripePaymentMethodId || ""
  )

  useEffect(() => {
    setMembershipPageVisited((parseInt(membershipPageVisited) || 0) + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user.dockwaPlusSubscription?.stripePaymentMethodId) {
      const paymentMethod = paymentMethods.find(
        (paymentMethod) =>
          paymentMethod.stripePaymentMethodId ===
          user.dockwaPlusSubscription?.stripePaymentMethodId
      )
      if (paymentMethod) {
        setCurrentPaymentMethod(paymentMethod.stripePaymentMethodId)
      }
    }
  }, [user.dockwaPlusSubscription?.stripePaymentMethodId, paymentMethods])

  const showToast = useToast()
  const { productInfo } = useDockwaPlusContext()
  const { data: dockwaPlusProductInformation } = productInfo

  const {
    data: subscriptionInvoicingData,
    isFetching: isLoadingSubscriptionInvoicingData,
    isError: isErrorSubscriptionInvoicingData,
  } = useQuery({
    queryKey: [
      "dockwaPlusSubscriptionInvoicingData",
      user.dockwaPlusSubscription,
    ],
    queryFn: ({ queryKey }) => {
      return queryDockwaPlusSubscriptionInvoicingData()
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!user.dockwaPlusSubscription,
    onError: (error) => {
      setInvoicingDataError(error.message)
    },
  })
  const nextPaymentAttempt = subscriptionInvoicingData?.next_payment_attempt

  const { mutate: restoreMembership, isLoading: restoringMembership } =
    useMutation(() => restoreDockwaPlusMembership(), {
      onSuccess: (data) => {
        showToast(
          `You've restored your Dockwa+ membership. You'll retain access to your benefits through ${data.subscription?.readableCurrentPeriodEnd}`,
          { type: "success" }
        )
        setUser(data.user)
        tracker.trackEvent("dockwa_plus:membership_restored")
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
        tracker.trackEvent("dockwa_plus:membership_restore_failed", {
          error: error.message,
        })
      },
    })

  const { mutate: updatePaymentMethod, isLoading: updatingPaymentMethod } =
    useMutation((data) => updateDockwaPlusPaymentMethod(data), {
      onSuccess: (data) => {
        setUser(data.user)
        queryClient.invalidateQueries("dockwaPlusSubscriptionInvoicingData")
        showToast("Dockwa+ membership payment method updated successfully!", {
          type: "success",
        })
        tracker.trackEvent("dockwa_plus:payment_method_updated")
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
        tracker.trackEvent("dockwa_plus:payment_method_update_failed", {
          error: error.message,
        })
      },
    })

  const getSubtotalInDollars = () => {
    if (!dockwaPlusProductInformation?.line_items) return null

    const subTotal = dockwaPlusProductInformation.line_items.find(
      (item) => item.type === "subtotal"
    )

    if (subTotal) {
      return formatCurrencyFromMinorUnits({ amount: subTotal.amount })
    }
  }

  const handleDockwaPlusSignup = () => {
    setIsSignupModalOpen(true)
    const { dockwa_plus_external_id: dockwaPlusExternalId } =
      dockwaPlusProductInformation

    tracker.trackEvent("dockwa_plus:checkout_screen_viewed", {
      dockwa_plus_external_id: dockwaPlusExternalId,
    })
  }

  const handlePaymentMethodChange = (paymentMethod) => {
    if (paymentMethod === ADD_NEW_PAYMENT_METHOD) {
      setCurrentPaymentMethod(ADD_NEW_PAYMENT_METHOD)
    } else if (
      paymentMethod !== user.dockwaPlusSubscription?.stripePaymentMethodId
    ) {
      // Only update if different from the stored subscription payment method
      showToast("Updating your Dockwa+ membership payment method.", {
        type: "info",
      })
      updatePaymentMethod({ stripePaymentMethodId: paymentMethod })
    } else {
      // Reset dropdown to stored value (e.g., when canceling "add new")
      setCurrentPaymentMethod(paymentMethod)
    }
  }

  const renderBlankState = () => (
    <div className="flex flex-col space-y-6">
      <span>
        Unlock exclusive discounts with our membership card for just{" "}
        {getSubtotalInDollars()} annually. With Dockwa+ you get access to:
      </span>
      <div className="flex flex-col space-y-6">
        <div className="flex items-center space-x-6">
          <i className="fa-solid fa-check text-teal-600" />
          <span>Competitive Dockage Rates</span>
        </div>
        <div className="flex items-center space-x-6">
          <i className="fa-solid fa-check text-teal-600" />
          <span>Unbeatable Fuel Prices</span>
        </div>
        <div className="flex items-center space-x-6">
          <i className="fa-solid fa-check text-teal-600" />
          <span>Exclusive Premium Features: Cruise Plan Tool and more</span>
        </div>
      </div>
      <div className="pt-4">
        <Button
          variant="primary"
          onClick={handleDockwaPlusSignup}
          disabled={
            !dockwaPlusProductInformation || dockwaPlusProductInformation.error
          }
        >
          Sign Up
        </Button>
      </div>
      <DockwaPlusMembershipSignupModal
        isOpen={isSignupModalOpen}
        setIsOpen={setIsSignupModalOpen}
        setUser={setUser}
        paymentMethods={paymentMethods}
        setPaymentMethods={setPaymentMethods}
      />
    </div>
  )

  const handleCancel = () => {
    tracker.trackEvent("dockwa_plus:cancel_membership_pressed")

    return setIsCancelModalOpen(true)
  }

  const handleRestore = () => {
    tracker.trackEvent("dockwa_plus:restore_membership_pressed")

    return restoreMembership()
  }

  const renderCurrentDetailsSection = () => {
    return (
      <div
        className="flex w-full flex-col gap-y-2"
        data-testid="dockwa-plus-current-status"
      >
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Status:</span>
          <Badge
            color={statusToken(user.dockwaPlusSubscription.status)}
            icon={isPastDue ? "triangle-exclamation" : null}
            iconStyle={isPastDue ? "regular" : undefined}
            text={humanize(user.dockwaPlusSubscription.status)}
            variant="small"
          />
        </div>
        <div className="my-3 w-full border-b" />
        <div className="font-semibold text-gray-900">
          {`Current Period ${isPastDue ? "- Payment Required" : ""}`}
        </div>
        {isErrorSubscriptionInvoicingData ? (
          <span className="text-red-600">{invoicingDataError}</span>
        ) : (
          <>
            <LineItems
              lineItems={
                subscriptionInvoicingData?.current_invoicing_line_items
              }
              loading={isLoadingSubscriptionInvoicingData}
              loadingItems={3}
            />
            {isPastDue && !!subscriptionInvoicingData ? (
              <AlertBanner type="error">
                {`Payment failed. ${
                  nextPaymentAttempt
                    ? `Dockwa will retry payment on
                      ${format(new Date(nextPaymentAttempt), "MMMM d")}, please`
                    : `Please`
                } update your payment method below to ensure membership continuity. If you have already updated your payment method you may disregard this message.`}
              </AlertBanner>
            ) : null}
          </>
        )}
      </div>
    )
  }

  const renderCancellationNotice = () => {
    return (
      <AlertBanner type="error">
        <div className="font-semibold">Subscription Cancellation Scheduled</div>
        {`You've cancelled your Dockwa+ membership. You'll retain access to your benefits through ${user.dockwaPlusSubscription.readableCurrentPeriodEnd}.`}
      </AlertBanner>
    )
  }

  const renderNextInvoicing = () => {
    const renewsToday = nextPaymentAttempt
      ? isToday(new Date(nextPaymentAttempt))
      : false

    return (
      <>
        {isErrorSubscriptionInvoicingData ? (
          <span className="text-red-600">{invoicingDataError}</span>
        ) : (
          <>
            {subscriptionInvoicingData && nextPaymentAttempt ? (
              <div className="flex items-center gap-2">
                <span className="font-semibold">Next billing:</span>
                {format(new Date(nextPaymentAttempt), "MMMM d, yyyy")}
                {renewsToday ? (
                  <Badge
                    text="Today"
                    color={statusToken("Today")}
                    variant="small"
                  />
                ) : null}
              </div>
            ) : null}

            <LineItems
              lineItems={
                subscriptionInvoicingData?.renewal_invoicing_line_items
              }
              loading={isLoadingSubscriptionInvoicingData}
              loadingItems={3}
            />
          </>
        )}
      </>
    )
  }

  const renderRenewalOption = () => {
    return (
      <div className="my-4 rounded-lg border border-gray-300 bg-gray-100 p-3">
        <span className="font-semibold">No renewal scheduled.</span>
        {` Your membership will end on ${user.dockwaPlusSubscription.readableCurrentPeriodEnd}`}
        <div className="center my-4 w-full">
          <Button
            variant="primary"
            onClick={handleRestore}
            isLoading={restoringMembership}
            fullWidth
          >
            {restoringMembership ? "Renewing" : "Renew Dockwa+ Subscription"}
          </Button>
        </div>
      </div>
    )
  }

  const renderRenewalSection = () => {
    return (
      <div
        className="flex w-full flex-col gap-y-2"
        data-testid="dockwa-plus-renewal"
      >
        <HeadingModalTitle>{`Renewal ${
          isScheduledForCancellation ? "Status" : "Information"
        }`}</HeadingModalTitle>{" "}
        {isScheduledForCancellation
          ? renderRenewalOption()
          : renderNextInvoicing()}
      </div>
    )
  }

  const renderPaymentMethodSection = () => {
    return (
      <div
        className="flex w-full flex-col gap-y-2"
        data-testid="dockwa-plus-payment-method"
      >
        <HeadingModalTitle>Payment Method</HeadingModalTitle>
        <SelectPaymentMethod
          acceptedPaymentMethods={["card"]}
          paymentMethods={cardPaymentMethods}
          paymentMethod={currentPaymentMethod}
          setPaymentMethod={handlePaymentMethodChange}
          setPaymentMethods={setPaymentMethods}
          suppressHeading={true}
          disabled={updatingPaymentMethod}
          trackingProps={{
            paymentMethodSelected: {
              eventName: "dockwa_plus:payment_method_selected",
              eventProps: {},
            },
            paymentMethodAdded: {
              eventName: "dockwa_plus:payment_method_added",
              eventProps: {},
            },
          }}
        />
      </div>
    )
  }

  const renderCancellationSection = () => {
    return (
      <>
        <div className="my-3 w-full border-b" />
        <div className="flex flex-col" data-testid="dockwa-plus-cancel">
          <button
            className="text-link w-min whitespace-nowrap text-blue-700"
            onClick={handleCancel}
          >
            Cancel Membership
          </button>
          <span className="text-gray-600">
            (No refunds will be given and you&apos;ll have access to your
            benefits through{" "}
            {user.dockwaPlusSubscription.readableCurrentPeriodEnd}.)
          </span>
        </div>
        <CancelDockwaPlusMembershipModal
          user={user}
          isOpen={isCancelModalOpen}
          setIsOpen={setIsCancelModalOpen}
          setUser={setUser}
          subscription={user.dockwaPlusSubscription}
        />
      </>
    )
  }

  const renderDockwaPlusSubscription = () => (
    <>
      <div className="flex flex-wrap gap-8 sm:flex-nowrap">
        <div className="w-full" data-testid="dockwa-plus-membership-card">
          <DockwaPlusMembershipCard />
        </div>
        {renderCurrentDetailsSection()}
      </div>
      {isScheduledForCancellation ? renderCancellationNotice() : null}
      <div className="my-3 w-full border-b" />
      <div className="flex flex-wrap gap-8 sm:flex-nowrap">
        {isPastDue ? null : renderRenewalSection()}
        {isScheduledForCancellation ? null : renderPaymentMethodSection()}
      </div>
      {isScheduledForCancellation ? null : renderCancellationSection()}
    </>
  )

  return (
    <div className="flex flex-col space-y-6 pb-8 text-sm">
      <div className="flex items-center space-x-2">
        <HeadingModalTitle>Dockwa+</HeadingModalTitle>{" "}
        {membershipPageVisited <= 3 && <Badge text="New!" color="info" />}
      </div>
      {user.dockwaPlusSubscription
        ? renderDockwaPlusSubscription()
        : renderBlankState()}
    </div>
  )
}

DockwaPlusSubscriptionContent.propTypes = {
  user: PropTypes.shape({
    dockwaPlusSubscription: PropTypes.shape({
      cancelAtPeriodEnd: PropTypes.bool.isRequired,
      currentPrice: PropTypes.string.isRequired,
      readableStartDate: PropTypes.string.isRequired,
      readableCurrentPeriodEnd: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      stripePaymentMethodId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  setUser: PropTypes.func.isRequired,
}

const DockwaPlusSubscription = (props) => {
  return (
    <DockwaPlusProvider>
      <DockwaPlusSubscriptionContent {...props} />
    </DockwaPlusProvider>
  )
}

DockwaPlusSubscription.propTypes = {
  user: PropTypes.shape({
    dockwaPlusSubscription: PropTypes.shape({
      cancelAtPeriodEnd: PropTypes.bool.isRequired,
      currentPrice: PropTypes.string.isRequired,
      readableStartDate: PropTypes.string.isRequired,
      readableCurrentPeriodEnd: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      stripePaymentMethodId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  setUser: PropTypes.func.isRequired,
}

export default DockwaPlusSubscription
